import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  School as SchoolIcon,
  ChevronRight as ChevronRightIcon,
  Assignment as LessonIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  CalendarToday as CalendarIcon,
  AccessTime as ClockIcon,
  AssignmentTurnedIn as CompletedIcon,
  WhatsApp as WhatsAppIcon,
  Lock as LockIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { api } from '../../services/api';

interface LessonProgress {
  lessonId: number;
  title: string;
  description: string;
  isCompleted: boolean;
  completionDate: string | null;
  notes: string | null;
  topicId?: number;
  topicName?: string;
  lessonDate: string;
}

interface StudentProgress {
  studentId: string;
  studentName: string;
  studentEmail: string;
  phoneNumber?: string;
  studentNumber?: string;
  licenseNumber?: string;
  licenseExpiryDate?: string;
  registrationDate?: string;
  totalLessons: number;
  completedLessons: number;
  lessons: LessonProgress[];
  licenseFrontImagePath?: string;
  licenseBackImagePath?: string;
  licenseStatus?: string;
  licenseRejectionReason?: string;
  theoryTestPassDate?: string;
  theoryTestPassReference?: string;
  dvlaShareCode?: string;
  eyesightTestPassed?: boolean;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  medicalConditions?: string;
  transmissionType?: string;
}

interface Topic {
  id: number;
  code: string;
  name: string;
  description: string;
}

const getImageUrl = (path: string | null) => {
  return api.getImageUrl(path);
};

const StudentManagement: React.FC = () => {
  const [students, setStudents] = useState<StudentProgress[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentProgress | null>(null);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // WhatsApp State
  const [isWhatsAppDialogOpen, setIsWhatsAppDialogOpen] = useState(false);
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);

  // Add Student Dialog State
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    licenseNumber: '',
    licenseExpiryDate: '',
    theoryTestPassDate: '',
    theoryTestPassReference: '',
    dvlaShareCode: '',
    eyesightTestPassed: false,
    emergencyContactName: '',
    emergencyContactPhone: '',
    medicalConditions: '',
    transmissionType: 'Manual'
  });
  const [isRegistering, setIsRegistering] = useState(false);

  // Success Credentials Dialog State
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [registeredCredentials, setRegisteredCredentials] = useState<{
    name: string;
    email: string;
    password: string;
  } | null>(null);

  // Edit Student Details Dialog State
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    licenseNumber: '',
    licenseExpiryDate: '',
    theoryTestPassDate: '',
    theoryTestPassReference: '',
    dvlaShareCode: '',
    eyesightTestPassed: false,
    emergencyContactName: '',
    emergencyContactPhone: '',
    medicalConditions: '',
    transmissionType: 'Manual'
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Password Reset dialog state
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Rejection dialog state
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImgUrl, setLightboxImgUrl] = useState('');
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);

  // Direct Schedule Lesson Dialog State
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    date: '',
    time: '09:00',
    duration: 60,
    notes: '',
    topicId: ''
  });
  const [isScheduling, setIsScheduling] = useState(false);

  // Edit Lesson Dialog State
  const [isLessonDialogOpen, setIsLessonDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState<LessonProgress | null>(null);
  const [lessonStatus, setLessonStatus] = useState({
    isCompleted: false,
    notes: '',
    topicId: ''
  });
  const [isUpdatingLesson, setIsUpdatingLesson] = useState(false);

  useEffect(() => {
    fetchStudents();
    fetchTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStudents = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getStudents();
      const data = response.data as any[];
      setStudents(data);
      
      // If a student was previously selected, update their data from the fresh list
      if (selectedStudent) {
        const updatedSelected = data.find(
          (s: any) => s.studentId === selectedStudent.studentId
        );
        if (updatedSelected) {
          setSelectedStudent(updatedSelected);
        }
      }
    } catch (err: any) {
      console.error('Error fetching students:', err);
      setError('An error occurred while loading students.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopics = async () => {
    try {
      const response = await api.getTopics();
      setTopics(response.data);
    } catch (err: any) {
      console.error('Error fetching topics:', err);
    }
  };

  const handleSelectStudent = (student: StudentProgress) => {
    setSelectedStudent(student);
  };

  // Password Generator
  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz';
    const upperChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^*';
    
    let password = '';
    password += upperChars[Math.floor(Math.random() * upperChars.length)];
    password += chars[Math.floor(Math.random() * chars.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    const allChars = chars + upperChars + numbers + symbols;
    for (let i = 0; i < 4; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle
    password = password.split('').sort(() => 0.5 - Math.random()).join('');
    setRegisterData(prev => ({ ...prev, password }));
  };

  // Share credentials text formatting
  const getShareText = () => {
    if (!registeredCredentials) return '';
    return `Hello ${registeredCredentials.name},\n\nYour registration in the Drive Management system has been successfully created. Your login details are as follows:\n\nWeb Address: ${window.location.origin}\nEmail: ${registeredCredentials.email}\nPassword: ${registeredCredentials.password}\n\nWe wish you great lessons!`;
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(getShareText());
    setSuccessMsg('Login credentials copied to clipboard.');
  };

  const handleWhatsAppShare = () => {
    const encodedText = encodeURIComponent(getShareText());
    window.open(`https://api.whatsapp.com/send?text=${encodedText}`, '_blank');
  };

  // Student Registration Handler
  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!registerData.firstName || !registerData.lastName || !registerData.email || !registerData.password) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      setIsRegistering(true);
      setError(null);
      
      const payload = {
        ...registerData,
        licenseExpiryDate: registerData.licenseExpiryDate || null,
        theoryTestPassDate: registerData.theoryTestPassDate || null
      };
      
      await api.registerStudent(payload);
      
      // Save credentials for the success modal
      setRegisteredCredentials({
        name: `${registerData.firstName} ${registerData.lastName}`,
        email: registerData.email,
        password: registerData.password
      });
      
      setIsAddDialogOpen(false);
      setIsSuccessDialogOpen(true);
      
      setRegisterData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phoneNumber: '',
        licenseNumber: '',
        licenseExpiryDate: '',
        theoryTestPassDate: '',
        theoryTestPassReference: '',
        dvlaShareCode: '',
        eyesightTestPassed: false,
        emergencyContactName: '',
        emergencyContactPhone: '',
        medicalConditions: '',
        transmissionType: 'Manual'
      });
      await fetchStudents();
    } catch (err: any) {
      console.error('Error registering student:', err);
      setError(err.response?.data?.message || 'Failed to create student registration.');
    } finally {
      setIsRegistering(false);
    }
  };

  // Edit Student Handler
  const handleOpenEditDialog = () => {
    if (!selectedStudent) return;
    const parts = selectedStudent.studentName.split(' ');
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';
    
    setEditData({
      firstName,
      lastName,
      phoneNumber: selectedStudent.phoneNumber || '',
      licenseNumber: selectedStudent.licenseNumber || '',
      licenseExpiryDate: selectedStudent.licenseExpiryDate ? selectedStudent.licenseExpiryDate.split('T')[0] : '',
      theoryTestPassDate: selectedStudent.theoryTestPassDate ? selectedStudent.theoryTestPassDate.split('T')[0] : '',
      theoryTestPassReference: selectedStudent.theoryTestPassReference || '',
      dvlaShareCode: selectedStudent.dvlaShareCode || '',
      eyesightTestPassed: selectedStudent.eyesightTestPassed ?? false,
      emergencyContactName: selectedStudent.emergencyContactName || '',
      emergencyContactPhone: selectedStudent.emergencyContactPhone || '',
      medicalConditions: selectedStudent.medicalConditions || '',
      transmissionType: selectedStudent.transmissionType || 'Manual'
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;
    if (!editData.firstName || !editData.lastName) {
      setError('First and last name are required.');
      return;
    }

    try {
      setIsSavingEdit(true);
      setError(null);
      
      const payload = {
        ...editData,
        licenseExpiryDate: editData.licenseExpiryDate || null,
        theoryTestPassDate: editData.theoryTestPassDate || null
      };
      
      await api.updateStudentDetails(selectedStudent.studentId, payload);
      setSuccessMsg('Student information updated successfully.');
      setIsEditDialogOpen(false);
      await fetchStudents();
    } catch (err: any) {
      console.error('Error updating student details:', err);
      setError(err.response?.data?.message || 'Failed to update student information.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Password Reset for Student
  const handleOpenPasswordDialog = () => {
    setNewPassword('');
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    try {
      setIsChangingPassword(true);
      setError(null);
      await api.changeStudentPassword(selectedStudent.studentId, newPassword);
      setSuccessMsg('Student password updated successfully.');
      setIsPasswordDialogOpen(false);
    } catch (err: any) {
      console.error('Error changing student password:', err);
      setError(err.response?.data?.message || 'Failed to update password.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // License Image Upload
  const handleLicenseUpload = async (e: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => {
    if (!selectedStudent) return;
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    try {
      setIsLoading(true);
      setError(null);
      await api.uploadLicenseImage(selectedStudent.studentId, side, file);
      setSuccessMsg(`License ${side === 'front' ? 'front' : 'back'} side uploaded successfully.`);
      await fetchStudents();
    } catch (err: any) {
      console.error('Error uploading license image:', err);
      setError(err.response?.data?.message || 'Failed to upload license image.');
    } finally {
      setIsLoading(false);
    }
  };

  // License Verification (Approve/Reject)
  const handleVerifyLicense = async (status: 'Approved' | 'Rejected', reason?: string) => {
    if (!selectedStudent) return;
    try {
      setIsLoading(true);
      setError(null);
      await api.verifyStudentLicense(selectedStudent.studentId, status, reason);
      setSuccessMsg(status === 'Approved' ? 'License approved successfully.' : 'License rejected.');
      setIsRejectDialogOpen(false);
      await fetchStudents();
    } catch (err: any) {
      console.error('Error verifying license:', err);
      setError(err.response?.data?.message || 'Failed to update license verification status.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRejectDialog = () => {
    setRejectionReason('');
    setIsRejectDialogOpen(true);
  };

  const handleRejectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejectionReason.trim()) {
      setError('Please specify a rejection reason.');
      return;
    }
    await handleVerifyLicense('Rejected', rejectionReason);
  };

  // Lightbox Handlers
  const handleOpenImageLightbox = (url: string) => {
    setLightboxImgUrl(url);
    setRotation(0);
    setScale(1);
    setIsLightboxOpen(true);
  };

  const handleRotateRight = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 1));
  };

  // Direct Lesson Scheduling Handler
  const handleScheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;
    if (!scheduleData.date || !scheduleData.time || !scheduleData.topicId) {
      setError('Please select date, time, and lesson topic.');
      return;
    }

    try {
      setIsScheduling(true);
      setError(null);
      const lessonDateTime = `${scheduleData.date}T${scheduleData.time}:00`;
      
      await api.scheduleLesson(selectedStudent.studentId, {
        lessonDate: lessonDateTime,
        duration: scheduleData.duration,
        notes: scheduleData.notes,
        topicId: Number(scheduleData.topicId)
      });

      setSuccessMsg('Lesson scheduled successfully.');
      setIsScheduleDialogOpen(false);
      setScheduleData({
        date: '',
        time: '09:00',
        duration: 60,
        notes: '',
        topicId: ''
      });
      await fetchStudents();
    } catch (err: any) {
      console.error('Error scheduling lesson:', err);
      setError(err.response?.data?.message || 'Failed to schedule lesson.');
    } finally {
      setIsScheduling(false);
    }
  };

  // Lesson Update Handler
  const handleEditLesson = (lesson: LessonProgress) => {
    setEditingLesson(lesson);
    setLessonStatus({
      isCompleted: lesson.isCompleted,
      notes: lesson.notes || '',
      topicId: lesson.topicId ? lesson.topicId.toString() : ''
    });
    setIsLessonDialogOpen(true);
  };

  const handleWhatsAppReminder = async (lesson: LessonProgress) => {
    if (!selectedStudent || !selectedStudent.phoneNumber) return;

    const matchedTopic = topics.find(t => t.id === lesson.topicId);
    const topicName = matchedTopic ? matchedTopic.name : 'Driving Lesson';
    
    const dateStr = new Date(lesson.lessonDate).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const text = `Hello ${selectedStudent.studentName}, as your driving instructor, I would like to remind you that our next lesson is scheduled for ${dateStr} with the topic '${topicName}'. See you!`;

    try {
      setError(null);
      setSuccessMsg('Sending reminder message...');
      await api.sendWhatsAppMessage(selectedStudent.studentId, text);
      setSuccessMsg('WhatsApp reminder sent successfully.');
    } catch (err: any) {
      console.error('Error sending WhatsApp reminder:', err);
      setError(err.response?.data?.message || 'Failed to send WhatsApp reminder. Please check if your WhatsApp connection is active.');
    }
  };

  const handleOpenWhatsAppDialog = () => {
    setWhatsAppMessage('');
    setIsWhatsAppDialogOpen(true);
  };

  const handleSendWhatsAppMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent || !whatsAppMessage.trim()) return;

    try {
      setIsSendingWhatsApp(true);
      setError(null);
      await api.sendWhatsAppMessage(selectedStudent.studentId, whatsAppMessage);
      setSuccessMsg('WhatsApp message sent successfully.');
      setIsWhatsAppDialogOpen(false);
    } catch (err: any) {
      console.error('Error sending WhatsApp message:', err);
      setError(err.response?.data?.message || 'Failed to send WhatsApp message. Please check if your WhatsApp connection is active.');
    } finally {
      setIsSendingWhatsApp(false);
    }
  };

  const handleSaveLessonStatus = async () => {
    if (!selectedStudent || !editingLesson) return;

    try {
      setIsUpdatingLesson(true);
      setError(null);
      await api.updateLessonStatus(
        selectedStudent.studentId,
        editingLesson.lessonId.toString(),
        lessonStatus.isCompleted,
        lessonStatus.notes,
        lessonStatus.topicId ? Number(lessonStatus.topicId) : undefined
      );
      
      setSuccessMsg('Lesson status updated successfully.');
      setIsLessonDialogOpen(false);
      await fetchStudents();
    } catch (err: any) {
      console.error('Error updating lesson:', err);
      setError(err.response?.data?.message || 'Failed to update lesson status.');
    } finally {
      setIsUpdatingLesson(false);
    }
  };

  if (isLoading && students.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  // Filter students based on search query
  const filteredStudents = students.filter(student =>
    student.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.studentEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (student.studentNumber && student.studentNumber.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          Student Management (UK DVSA)
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={() => setIsAddDialogOpen(true)}
          sx={{ borderRadius: 2, fontWeight: 'bold', px: 3 }}
        >
          Add New Student
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Left Column: Student List */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#37474f' }}>
            Your Students ({filteredStudents.length})
          </Typography>
          <TextField
            placeholder="Name, email or student no..."
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
          />
          <Divider sx={{ mb: 2 }} />
          
          {filteredStudents.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#fcfcfc' }}>
              <Typography color="textSecondary" variant="body2" sx={{ fontStyle: 'italic' }}>
                No student found matching the search criteria or registered.
              </Typography>
            </Paper>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {filteredStudents.map((student) => {
                const isSelected = selectedStudent?.studentId === student.studentId;
                const progress = student.totalLessons > 0 
                  ? Math.round((student.completedLessons / student.totalLessons) * 100) 
                  : 0;
                  
                return (
                  <Card
                    key={student.studentId}
                    onClick={() => handleSelectStudent(student)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 3,
                      border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.02)' : '#fff',
                      transition: 'all 0.2s',
                      boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
                      '&:hover': {
                        borderColor: '#1976d2',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" alignItems="center" gap={2} mb={1.5}>
                        <Avatar sx={{ bgcolor: isSelected ? 'primary.main' : 'grey.400' }}>
                          <PersonIcon />
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} noWrap>
                            {student.studentName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="flex" alignItems="center" gap={0.5} noWrap>
                            <EmailIcon fontSize="inherit" /> {student.studentEmail}
                          </Typography>
                        </Box>
                        <ChevronRightIcon color={isSelected ? 'primary' : 'disabled'} />
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                          Syllabus Completion
                        </Typography>
                        <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                          {student.completedLessons}/{student.totalLessons} ({progress}%)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={progress} 
                        sx={{ height: 6, borderRadius: 3, bgcolor: '#e0e0e0' }} 
                      />
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Grid>

        {/* Right Column: Selected Student Details & Lessons */}
        <Grid size={{ xs: 12, md: 8 }}>
          {selectedStudent ? (
            <Box display="flex" flexDirection="column" gap={3}>
              <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                      {selectedStudent.studentName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon fontSize="inherit" /> {selectedStudent.studentEmail}
                    </Typography>
                    {selectedStudent.phoneNumber && (
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon fontSize="inherit" /> {selectedStudent.phoneNumber}
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      startIcon={<LockIcon />}
                      onClick={handleOpenPasswordDialog}
                      sx={{ borderRadius: 2, fontWeight: 'bold', px: 2 }}
                    >
                      Change Password
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={handleOpenEditDialog}
                      sx={{ borderRadius: 2, fontWeight: 'bold', px: 2 }}
                    >
                      Edit
                    </Button>
                    {selectedStudent.phoneNumber && (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<WhatsAppIcon />}
                        onClick={handleOpenWhatsAppDialog}
                        sx={{ borderRadius: 2, fontWeight: 'bold', px: 2, backgroundColor: '#25D366', '&:hover': { backgroundColor: '#1ebe57' } }}
                      >
                        WhatsApp Message
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<CalendarIcon />}
                      onClick={() => setIsScheduleDialogOpen(true)}
                      sx={{ borderRadius: 2, fontWeight: 'bold', px: 2 }}
                    >
                      Schedule Lesson
                    </Button>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Student No:</strong> {selectedStudent.studentNumber || '-'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Registration Date:</strong> {selectedStudent.registrationDate ? new Date(selectedStudent.registrationDate).toLocaleDateString('en-GB') : '-'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>License No:</strong> {selectedStudent.licenseNumber || 'Not specified'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>License Expiry:</strong> {selectedStudent.licenseExpiryDate ? new Date(selectedStudent.licenseExpiryDate).toLocaleDateString('en-GB') : 'Not specified'}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon fontSize="small" /> UK Regulations Info
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Theory Test Date:</strong> {selectedStudent.theoryTestPassDate ? new Date(selectedStudent.theoryTestPassDate).toLocaleDateString('en-GB') : 'Not passed'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Theory Test Ref:</strong> {selectedStudent.theoryTestPassReference || '-'}
                    </Typography>
                  </Grid>
                  {selectedStudent.theoryTestPassDate && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ color: '#d32f2f', fontWeight: 'medium' }}>
                        <strong>Practical Test Deadline:</strong> {(() => {
                          const passDate = new Date(selectedStudent.theoryTestPassDate);
                          passDate.setFullYear(passDate.getFullYear() + 2);
                          return passDate.toLocaleDateString('en-GB');
                        })()} (2 Years)
                      </Typography>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>DVLA Share Code:</strong> {selectedStudent.dvlaShareCode || '-'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Eyesight Test (20m):</strong> {selectedStudent.eyesightTestPassed ? 'Passed / Approved' : 'Not passed'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Transmission Type:</strong> {selectedStudent.transmissionType === 'Automatic' ? 'Automatic' : 'Manual'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Emergency Contact:</strong> {selectedStudent.emergencyContactName ? `${selectedStudent.emergencyContactName} (${selectedStudent.emergencyContactPhone || '-'})` : '-'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Medical Conditions:</strong> {selectedStudent.medicalConditions || 'None'}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1.5 }}>
                  Driving License Verification Process
                </Typography>
                
                <Box display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
                  <Typography variant="body2" fontWeight="bold">Verification Status:</Typography>
                  {selectedStudent.licenseStatus === 'Approved' && <Chip label="Approved" color="success" size="small" />}
                  {selectedStudent.licenseStatus === 'Pending' && <Chip label="Pending Approval" color="warning" size="small" />}
                  {selectedStudent.licenseStatus === 'Rejected' && <Chip label="Rejected" color="error" size="small" />}
                  {(selectedStudent.licenseStatus === 'None' || !selectedStudent.licenseStatus) && <Chip label="Not Uploaded" color="default" size="small" />}
                </Box>

                {selectedStudent.licenseRejectionReason && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    <strong>Rejection Reason:</strong> {selectedStudent.licenseRejectionReason}
                  </Alert>
                )}

                <Grid container spacing={3} sx={{ mb: 2 }}>
                  {/* Front Side */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold', color: '#37474f' }}>Driving License Front Side</Typography>
                      {selectedStudent.licenseFrontImagePath ? (
                        <Box sx={{ position: 'relative', width: '100%', height: 140, bgcolor: '#f5f5f5', borderRadius: 2, overflow: 'hidden', cursor: 'pointer', border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                             onClick={() => handleOpenImageLightbox(selectedStudent.licenseFrontImagePath!)}>
                          <img src={getImageUrl(selectedStudent.licenseFrontImagePath)} alt="License Front" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fcfcfc', borderRadius: 2, border: '1px dashed #ccc', mb: 1 }}>
                          <Typography variant="caption" color="textSecondary">Front side image not uploaded</Typography>
                        </Box>
                      )}
                      <Button variant="outlined" component="label" size="small" sx={{ mt: 1.5, borderRadius: 2, fontWeight: 'bold' }}>
                        Upload Front Side
                        <input type="file" accept="image/*" hidden onChange={(e) => handleLicenseUpload(e, 'front')} />
                      </Button>
                    </Card>
                  </Grid>

                  {/* Back Side */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold', color: '#37474f' }}>Driving License Back Side</Typography>
                      {selectedStudent.licenseBackImagePath ? (
                        <Box sx={{ position: 'relative', width: '100%', height: 140, bgcolor: '#f5f5f5', borderRadius: 2, overflow: 'hidden', cursor: 'pointer', border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                             onClick={() => handleOpenImageLightbox(selectedStudent.licenseBackImagePath!)}>
                          <img src={getImageUrl(selectedStudent.licenseBackImagePath)} alt="License Back" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fcfcfc', borderRadius: 2, border: '1px dashed #ccc', mb: 1 }}>
                          <Typography variant="caption" color="textSecondary">Back side image not uploaded</Typography>
                        </Box>
                      )}
                      <Button variant="outlined" component="label" size="small" sx={{ mt: 1.5, borderRadius: 2, fontWeight: 'bold' }}>
                        Upload Back Side
                        <input type="file" accept="image/*" hidden onChange={(e) => handleLicenseUpload(e, 'back')} />
                      </Button>
                    </Card>
                  </Grid>
                </Grid>

                {/* Verification Approve/Reject Buttons */}
                {selectedStudent.licenseStatus === 'Pending' && (
                  <Box display="flex" justifyContent="flex-end" gap={1.5} mt={1.5}>
                    <Button variant="outlined" color="error" size="small" sx={{ borderRadius: 2, fontWeight: 'bold', px: 3 }} onClick={handleOpenRejectDialog}>
                      Reject
                    </Button>
                    <Button variant="contained" color="success" size="small" sx={{ borderRadius: 2, fontWeight: 'bold', px: 3 }} onClick={() => handleVerifyLicense('Approved')}>
                      Approve
                    </Button>
                  </Box>
                )}
                {selectedStudent.licenseStatus === 'Approved' && (
                  <Box display="flex" justifyContent="flex-end" mt={1.5}>
                    <Button variant="outlined" color="error" size="small" sx={{ borderRadius: 2, fontWeight: 'bold' }} onClick={handleOpenRejectDialog}>
                      Remove Verification / Reject
                    </Button>
                  </Box>
                )}
                {selectedStudent.licenseStatus === 'Rejected' && (
                  <Box display="flex" justifyContent="flex-end" mt={1.5}>
                    <Button variant="contained" color="success" size="small" sx={{ borderRadius: 2, fontWeight: 'bold' }} onClick={() => handleVerifyLicense('Approved')}>
                      Approve (Review Again)
                    </Button>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid size={4}>
                    <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa', borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#37474f' }}>
                        {selectedStudent.totalLessons}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Total Lessons
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={4}>
                    <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                        {selectedStudent.completedLessons}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Completed
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={4}>
                    <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(25, 118, 210, 0.05)', borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {selectedStudent.totalLessons > 0 
                          ? Math.round((selectedStudent.completedLessons / selectedStudent.totalLessons) * 100)
                          : 0}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Completion Rate
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#37474f', mt: 1 }}>
                Lesson History and Scheduling
              </Typography>
              
              {selectedStudent.lessons.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#fcfcfc' }}>
                  <Typography color="textSecondary" variant="body2">
                    There are no scheduled driving lessons for this student.
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Syllabus Topic / Lesson</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date / Time</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedStudent.lessons.map((lesson) => {
                        const matchedTopic = topics.find(t => t.id === lesson.topicId);
                        
                        return (
                          <TableRow key={lesson.lessonId} hover>
                            <TableCell sx={{ fontWeight: 'medium' }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <LessonIcon fontSize="small" color="action" />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {lesson.title}
                                  </Typography>
                                  {matchedTopic && (
                                    <Chip
                                      label={matchedTopic.code}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', height: 18, mt: 0.5 }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                             <TableCell>
                               <Box display="flex" flexDirection="column" gap={0.5}>
                                 <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                   {new Date(lesson.lessonDate).toLocaleDateString('en-GB', {
                                     day: 'numeric',
                                     month: 'short',
                                     year: 'numeric',
                                     hour: '2-digit',
                                     minute: '2-digit'
                                   })}
                                 </Typography>
                                 <Typography variant="caption" color="textSecondary">
                                   Duration: {lesson.description}
                                 </Typography>
                               </Box>
                             </TableCell>
                             <TableCell>
                               <Chip
                                 label={lesson.isCompleted ? 'Completed' : 'Scheduled'}
                                 color={lesson.isCompleted ? 'success' : 'default'}
                                 size="small"
                                 sx={{ fontWeight: 'bold' }}
                               />
                             </TableCell>
                             <TableCell sx={{ maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                               {lesson.notes || <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>No notes</Typography>}
                             </TableCell>
                             <TableCell sx={{ textAlign: 'center' }}>
                               <Box display="flex" justifyContent="center" gap={1}>
                                 <Button
                                   variant="outlined"
                                   color="primary"
                                   size="small"
                                   startIcon={<EditIcon />}
                                   onClick={() => handleEditLesson(lesson)}
                                   sx={{ borderRadius: 1.5 }}
                                 >
                                   Update
                                 </Button>
                                 {selectedStudent.phoneNumber && !lesson.isCompleted && (
                                   <Button
                                     variant="outlined"
                                     color="success"
                                     size="small"
                                     startIcon={<WhatsAppIcon />}
                                     onClick={() => handleWhatsAppReminder(lesson)}
                                     sx={{ borderRadius: 1.5, borderColor: '#2e7d32', color: '#2e7d32', '&:hover': { borderColor: '#1b5e20', backgroundColor: 'rgba(46, 125, 50, 0.04)' } }}
                                   >
                                     Remind
                                   </Button>
                                 )}
                               </Box>
                             </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : (
            <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3, border: '2px dashed #e0e0e0', backgroundColor: '#fafafa' }}>
              <SchoolIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                No Student Selected
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Select a student from the left panel to view their details, lesson history, and syllabus progress.
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Add Student Dialog */}
      <Dialog 
        open={isAddDialogOpen} 
        onClose={() => setIsAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={handleRegisterSubmit}>
          <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e' }}>Register New Student</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="First Name"
                  required
                  fullWidth
                  value={registerData.firstName}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, firstName: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Last Name"
                  required
                  fullWidth
                  value={registerData.lastName}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, lastName: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Email Address"
                  type="email"
                  required
                  fullWidth
                  value={registerData.email}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Phone Number"
                  fullWidth
                  value={registerData.phoneNumber}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <TextField
                    label="Login Password"
                    type="text"
                    required
                    fullWidth
                    value={registerData.password}
                    onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                    helperText="Student's login password."
                  />
                  <Button 
                    variant="outlined" 
                    onClick={generatePassword}
                    sx={{ mt: 0.5, py: 1.8, whiteSpace: 'nowrap', borderRadius: 2, fontWeight: 'bold' }}
                  >
                    Generate Password
                  </Button>
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }}><Chip label="UK License & Exam Details" size="small" /></Divider>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="License Number"
                  fullWidth
                  value={registerData.licenseNumber}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, licenseNumber: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="License Expiry Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={registerData.licenseExpiryDate}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, licenseExpiryDate: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Theory Test Pass Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={registerData.theoryTestPassDate}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, theoryTestPassDate: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Theory Test Ref No"
                  fullWidth
                  value={registerData.theoryTestPassReference}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, theoryTestPassReference: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="DVLA Share Code"
                  fullWidth
                  value={registerData.dvlaShareCode}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, dvlaShareCode: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="register-transmission-label">Transmission Type</InputLabel>
                  <Select
                    labelId="register-transmission-label"
                    id="register-transmission"
                    value={registerData.transmissionType}
                    label="Transmission Type"
                    onChange={(e) => setRegisterData(prev => ({ ...prev, transmissionType: e.target.value }))}
                  >
                    <MenuItem value="Manual">Manual</MenuItem>
                    <MenuItem value="Automatic">Automatic</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={registerData.eyesightTestPassed}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, eyesightTestPassed: e.target.checked }))}
                    />
                  }
                  label="Eyesight Test Passed (20 Meter license plate reading)"
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }}><Chip label="Emergency & Health" size="small" /></Divider>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Emergency Contact Person"
                  fullWidth
                  value={registerData.emergencyContactName}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, emergencyContactName: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Emergency Contact Phone"
                  fullWidth
                  value={registerData.emergencyContactPhone}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, emergencyContactPhone: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Medical Conditions Affecting Driving"
                  fullWidth
                  multiline
                  rows={2}
                  value={registerData.medicalConditions}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, medicalConditions: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setIsAddDialogOpen(false)} color="inherit" disabled={isRegistering}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isRegistering}>
              {isRegistering ? <CircularProgress size={24} color="inherit" /> : 'Complete Registration'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Registration Success Sharing Dialog */}
      <Dialog
        open={isSuccessDialogOpen}
        onClose={() => setIsSuccessDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CompletedIcon color="success" /> Registration Successful!
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Student registration has been created. You can share the login credentials with the student:
          </Typography>
          
          {registeredCredentials && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 2, mb: 3 }}>
              <Box mb={1.5}>
                <Typography variant="caption" color="textSecondary" display="block">Student Name</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{registeredCredentials.name}</Typography>
              </Box>
              <Box mb={1.5}>
                <Typography variant="caption" color="textSecondary" display="block">Email</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{registeredCredentials.email}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Login Password</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#d32f2f', fontFamily: 'monospace' }}>{registeredCredentials.password}</Typography>
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setIsSuccessDialogOpen(false)} color="inherit">
            Close
          </Button>
          <Box display="flex" gap={1}>
            <Button 
              variant="outlined" 
              startIcon={<CopyIcon />} 
              onClick={handleCopyToClipboard}
              sx={{ borderRadius: 2 }}
            >
              Copy
            </Button>
            <Button 
              variant="contained" 
              color="success" 
              startIcon={<ShareIcon />} 
              onClick={handleWhatsAppShare}
              sx={{ borderRadius: 2 }}
            >
              WhatsApp
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Edit Student Details Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={handleEditSubmit}>
          <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e' }}>Edit Student Details</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="First Name"
                  required
                  fullWidth
                  value={editData.firstName}
                  onChange={(e) => setEditData(prev => ({ ...prev, firstName: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Last Name"
                  required
                  fullWidth
                  value={editData.lastName}
                  onChange={(e) => setEditData(prev => ({ ...prev, lastName: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Phone Number"
                  fullWidth
                  value={editData.phoneNumber}
                  onChange={(e) => setEditData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }}><Chip label="UK License & Exam Details" size="small" /></Divider>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="License Number"
                  fullWidth
                  value={editData.licenseNumber}
                  onChange={(e) => setEditData(prev => ({ ...prev, licenseNumber: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="License Expiry Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editData.licenseExpiryDate}
                  onChange={(e) => setEditData(prev => ({ ...prev, licenseExpiryDate: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Theory Test Pass Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editData.theoryTestPassDate}
                  onChange={(e) => setEditData(prev => ({ ...prev, theoryTestPassDate: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Theory Test Ref No"
                  fullWidth
                  value={editData.theoryTestPassReference}
                  onChange={(e) => setEditData(prev => ({ ...prev, theoryTestPassReference: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="DVLA Share Code"
                  fullWidth
                  value={editData.dvlaShareCode}
                  onChange={(e) => setEditData(prev => ({ ...prev, dvlaShareCode: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="edit-transmission-label">Transmission Type</InputLabel>
                  <Select
                    labelId="edit-transmission-label"
                    id="edit-transmission"
                    value={editData.transmissionType}
                    label="Transmission Type"
                    onChange={(e) => setEditData(prev => ({ ...prev, transmissionType: e.target.value }))}
                  >
                    <MenuItem value="Manual">Manual</MenuItem>
                    <MenuItem value="Automatic">Automatic</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editData.eyesightTestPassed}
                      onChange={(e) => setEditData(prev => ({ ...prev, eyesightTestPassed: e.target.checked }))}
                    />
                  }
                  label="Eyesight Test Passed (20 Meter license plate reading)"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }}><Chip label="Emergency & Health" size="small" /></Divider>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Emergency Contact Person"
                  fullWidth
                  value={editData.emergencyContactName}
                  onChange={(e) => setEditData(prev => ({ ...prev, emergencyContactName: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Emergency Contact Phone"
                  fullWidth
                  value={editData.emergencyContactPhone}
                  onChange={(e) => setEditData(prev => ({ ...prev, emergencyContactPhone: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Medical Conditions Affecting Driving"
                  fullWidth
                  multiline
                  rows={2}
                  value={editData.medicalConditions}
                  onChange={(e) => setEditData(prev => ({ ...prev, medicalConditions: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setIsEditDialogOpen(false)} color="inherit" disabled={isSavingEdit}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isSavingEdit}>
              {isSavingEdit ? <CircularProgress size={24} color="inherit" /> : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Direct Lesson Scheduling Dialog */}
      <Dialog
        open={isScheduleDialogOpen}
        onClose={() => setIsScheduleDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={handleScheduleSubmit}>
          <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e' }}>Schedule New Lesson</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2.5} sx={{ mt: 1 }}>
              {selectedStudent && (
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                  Student: {selectedStudent.studentName}
                </Typography>
              )}
              
              <TextField
                label="Syllabus Topic (DVSA)"
                select
                required
                fullWidth
                value={scheduleData.topicId}
                onChange={(e) => setScheduleData(prev => ({ ...prev, topicId: e.target.value }))}
                helperText="Select the topic of the planned lesson from the UK DVSA syllabus."
              >
                {topics.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <strong>{t.code}</strong> - {t.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Lesson Date"
                type="date"
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={scheduleData.date}
                onChange={(e) => setScheduleData(prev => ({ ...prev, date: e.target.value }))}
              />
              <TextField
                label="Lesson Time"
                type="time"
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={scheduleData.time}
                onChange={(e) => setScheduleData(prev => ({ ...prev, time: e.target.value }))}
              />
              <TextField
                label="Lesson Duration (Minutes)"
                select
                required
                fullWidth
                value={scheduleData.duration}
                onChange={(e) => setScheduleData(prev => ({ ...prev, duration: Number(e.target.value) }))}
              >
                <MenuItem value={60}>60 Minutes (1 Hour)</MenuItem>
                <MenuItem value={90}>90 Minutes (1.5 Hours)</MenuItem>
                <MenuItem value={120}>120 Minutes (2 Hours)</MenuItem>
              </TextField>
              <TextField
                label="Planning Notes"
                multiline
                rows={3}
                fullWidth
                placeholder="Special lesson notes, practice route etc..."
                value={scheduleData.notes}
                onChange={(e) => setScheduleData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setIsScheduleDialogOpen(false)} color="inherit" disabled={isScheduling}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isScheduling}>
              {isScheduling ? <CircularProgress size={24} color="inherit" /> : 'Create Lesson'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Update Lesson Status Dialog */}
      <Dialog
        open={isLessonDialogOpen}
        onClose={() => setIsLessonDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Update Lesson Status</DialogTitle>
        <DialogContent dividers>
          {editingLesson && (
            <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {editingLesson.title}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Time/Duration: {editingLesson.description}
              </Typography>
              
              <TextField
                label="Syllabus Topic (DVSA)"
                select
                fullWidth
                value={lessonStatus.topicId}
                onChange={(e) => setLessonStatus(prev => ({ ...prev, topicId: e.target.value }))}
                helperText="You can update the DVSA topic covered in the lesson."
              >
                <MenuItem value=""><em>No Topic / Free Lesson</em></MenuItem>
                {topics.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <strong>{t.code}</strong> - {t.name}
                  </MenuItem>
                ))}
              </TextField>

              <Button
                variant={lessonStatus.isCompleted ? "contained" : "outlined"}
                color={lessonStatus.isCompleted ? "success" : "primary"}
                onClick={() => setLessonStatus(prev => ({ ...prev, isCompleted: !prev.isCompleted }))}
                fullWidth
                size="large"
                sx={{ 
                  py: 1.5, 
                  borderRadius: 2, 
                  fontWeight: 'bold',
                  boxShadow: lessonStatus.isCompleted ? '0 4px 12px rgba(76, 175, 80, 0.2)' : 'none',
                  textTransform: 'none'
                }}
              >
                {lessonStatus.isCompleted ? "Lesson Completed ✓" : "Mark Lesson as Completed"}
              </Button>

              <TextField
                label="Lesson Notes"
                multiline
                rows={3}
                fullWidth
                placeholder="Student performance, weaknesses, or notes for the next lesson..."
                value={lessonStatus.notes}
                onChange={(e) => setLessonStatus(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setIsLessonDialogOpen(false)} color="inherit" disabled={isUpdatingLesson}>
            Cancel
          </Button>
          <Button onClick={handleSaveLessonStatus} variant="contained" color="primary" disabled={isUpdatingLesson}>
            {isUpdatingLesson ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Student Password Dialog */}
      <Dialog 
        open={isPasswordDialogOpen} 
        onClose={() => setIsPasswordDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={handlePasswordSubmit}>
          <DialogTitle sx={{ fontWeight: 'bold', color: '#e65100' }}>Change Student Password</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Define a new password for student <strong>{selectedStudent?.studentName}</strong>.
              </Typography>
              <TextField
                label="New Password"
                type="text"
                required
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText="Must be at least 6 characters."
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setIsPasswordDialogOpen(false)} color="inherit" disabled={isChangingPassword}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="warning" disabled={isChangingPassword}>
              {isChangingPassword ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Reject License Dialog */}
      <Dialog 
        open={isRejectDialogOpen} 
        onClose={() => setIsRejectDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={handleRejectSubmit}>
          <DialogTitle sx={{ fontWeight: 'bold', color: '#c62828' }}>Reject License</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Enter the reason for rejecting the driving license to be sent to the student.
              </Typography>
              <TextField
                label="Rejection Reason"
                required
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., Image is blurry/unreadable or details do not match..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setIsRejectDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="error">
              Reject License
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Driving License Document Review Dialog */}
      <Dialog
        open={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold' }}>
          <span>Driving License Document Review</span>
          <Box display="flex" gap={1}>
            <IconButton onClick={handleZoomIn} color="primary" title="Zoom In">
              <ZoomInIcon />
            </IconButton>
            <IconButton onClick={handleZoomOut} color="primary" title="Zoom Out">
              <ZoomOutIcon />
            </IconButton>
            <IconButton onClick={handleRotateRight} color="primary" title="Rotate">
              <RotateIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, bgcolor: '#fafafa', overflow: 'auto', p: 2 }}>
          <Box sx={{ 
            transition: 'transform 0.2s ease', 
            transform: `rotate(${rotation}deg) scale(${scale})`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '70vh',
            maxWidth: '100%'
          }}>
            <img 
              src={getImageUrl(lightboxImgUrl)} 
              alt="License Large View" 
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }} 
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setIsLightboxOpen(false)} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Message composing Dialog */}
      <Dialog
        open={isWhatsAppDialogOpen}
        onClose={() => setIsWhatsAppDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} /> Send WhatsApp Message
        </DialogTitle>
        <form onSubmit={handleSendWhatsAppMessage}>
          <DialogContent>
            {selectedStudent && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Recipient: <strong>{selectedStudent.studentName}</strong> ({selectedStudent.phoneNumber})
              </Typography>
            )}
            <TextField
              required
              fullWidth
              multiline
              rows={4}
              label="Your Message"
              placeholder="Message you want to send to your student..."
              value={whatsAppMessage}
              onChange={(e) => setWhatsAppMessage(e.target.value)}
              disabled={isSendingWhatsApp}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setIsWhatsAppDialogOpen(false)} disabled={isSendingWhatsApp}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={isSendingWhatsApp || !whatsAppMessage.trim()}
              sx={{ backgroundColor: '#25D366', '&:hover': { backgroundColor: '#1ebe57' } }}
            >
              {isSendingWhatsApp ? <CircularProgress size={24} color="inherit" /> : 'Send'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMsg(null)} severity="success" sx={{ width: '100%', borderRadius: 2 }}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StudentManagement;
